/**
 * Quality Filter — the factory's reject gate.
 *
 * Wraps the Design Engine's validation + quality score into a single
 * pass/fail decision. Product rule: score below 90 ⇒ reject. The checks
 * the score already aggregates (hierarchy, balance, alignment, contrast,
 * whitespace, typography, composition, premium feel) are surfaced in the
 * failure reasons so rejects are explainable.
 */

export const DEFAULT_MIN_SCORE = 90;

/**
 * Evaluate a generateTemplate() result.
 * options = { minScore = 90, requireValid = true, minMetric = 0 }
 * minMetric optionally rejects any template whose single weakest breakdown
 * metric falls below a floor, even when the aggregate passes.
 *
 * Returns { passed, score, premiumFeel, reasons[] }.
 */
export const evaluateQuality = (result, { minScore = DEFAULT_MIN_SCORE, requireValid = true, minMetric = 0 } = {}) => {
  const reasons = [];
  const { quality, validation } = result;

  if (requireValid && !validation.valid) {
    reasons.push(
      `validation failed: ${validation.errors.map((e) => e.code).join(', ')}`
    );
  }
  if (quality.score < minScore) {
    const weakest = Object.entries(quality.breakdown)
      .sort((a, b) => a[1] - b[1])
      .slice(0, 2)
      .map(([metric, value]) => `${metric} ${value}`)
      .join(', ');
    reasons.push(`score ${quality.score} < ${minScore} (weakest: ${weakest})`);
  }
  if (minMetric > 0) {
    Object.entries(quality.breakdown).forEach(([metric, value]) => {
      if (value < minMetric) reasons.push(`metric "${metric}" ${value} < ${minMetric}`);
    });
  }

  return {
    passed: reasons.length === 0,
    score: quality.score,
    premiumFeel: quality.premiumFeel,
    reasons,
  };
};
